import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, TrendingUp, Clock, CheckCircle, Plus, Trash2, RefreshCw, Gem, Package } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CommissionsPage() {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialog, setAddDialog] = useState(false);
  const [newCommission, setNewCommission] = useState({ deal_id: '', entity_type: 'deal', amount: '', percentage: 1.0, notes: '' });
  const [deals, setDeals] = useState([]);
  const [minerals, setMinerals] = useState([]);
  const [generating, setGenerating] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'internal';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [commRes, dealsRes, mineralsRes] = await Promise.all([
        axios.get(`${API}/commissions`, { headers: getAuthHeaders() }),
        axios.get(`${API}/deals`, { headers: getAuthHeaders() }),
        axios.get(`${API}/minerals`, { headers: getAuthHeaders() })
      ]);
      setCommissions(commRes.data);
      setDeals(dealsRes.data);
      setMinerals(mineralsRes.data);
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.patch(`${API}/commissions/${id}`, { status }, { headers: getAuthHeaders() });
      toast.success(`Commission marked as ${status}`);
      fetchData();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/commissions/${id}`, { headers: getAuthHeaders() });
      toast.success('Commission deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/commissions/generate`, { percentage: 1.0 }, { headers: getAuthHeaders() });
      toast.success(`Generated ${res.data.created} new commission(s) at 1% rate`);
      fetchData();
    } catch {
      toast.error('Failed to generate');
    } finally {
      setGenerating(false);
    }
  };

  const handleAdd = async () => {
    try {
      const entity = newCommission.entity_type === 'mineral'
        ? minerals.find(m => m.id === newCommission.deal_id)
        : deals.find(d => d.id === newCommission.deal_id);
      const entityName = newCommission.entity_type === 'mineral' ? entity?.commodity : entity?.name;

      await axios.post(`${API}/commissions`, {
        deal_id: newCommission.deal_id,
        entity_type: newCommission.entity_type,
        entity_name: entityName || '',
        amount: Number(newCommission.amount),
        percentage: Number(newCommission.percentage),
        notes: newCommission.notes,
      }, { headers: getAuthHeaders() });
      toast.success('Commission created');
      setAddDialog(false);
      setNewCommission({ deal_id: '', entity_type: 'deal', amount: '', percentage: 1.0, notes: '' });
      fetchData();
    } catch {
      toast.error('Failed to create');
    }
  };

  const getEntityName = (c) => {
    if (c.entity_name) return c.entity_name;
    if (c.entity_type === 'mineral') {
      const m = minerals.find(m => m.id === c.deal_id);
      return m?.commodity || c.deal_id?.slice(0, 8);
    }
    const d = deals.find(d => d.id === c.deal_id);
    return d?.name || c.deal_id?.slice(0, 8);
  };

  const statusColors = {
    pending: 'border-yellow-400 text-yellow-700',
    invoiced: 'border-blue-400 text-blue-700',
    paid: 'border-green-400 text-green-700',
    confirmed: 'border-blue-400 text-blue-700',
  };

  const totalAmount = commissions.reduce((a, c) => a + c.amount, 0);
  const paidAmount = commissions.filter(c => c.status === 'paid').reduce((a, c) => a + c.amount, 0);
  const pendingAmount = commissions.filter(c => c.status === 'pending').reduce((a, c) => a + c.amount, 0);
  const invoicedAmount = commissions.filter(c => c.status === 'invoiced').reduce((a, c) => a + c.amount, 0);

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading commissions...</div></div>;

  const entityOptions = newCommission.entity_type === 'mineral'
    ? minerals.map(m => ({ id: m.id, name: m.commodity }))
    : deals.map(d => ({ id: d.id, name: d.name }));

  return (
    <div className="p-6 space-y-6" data-testid="commissions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Commission Tracking</h1>
          <p className="text-muted-foreground">Track brokerage fees across deals and mineral listings (1% default rate)</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleGenerate} disabled={generating} data-testid="generate-commissions-btn">
              <RefreshCw size={14} className={`mr-1 ${generating ? 'animate-spin' : ''}`} />Auto-Generate
            </Button>
            <Button onClick={() => setAddDialog(true)} data-testid="add-commission-btn">
              <Plus size={14} className="mr-1" />Add Commission
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Total Commissions</p><p className="text-2xl font-bold font-mono">{formatCurrency(totalAmount)}</p></div><DollarSign className="h-8 w-8 text-muted-foreground" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Paid</p><p className="text-2xl font-bold font-mono text-green-600">{formatCurrency(paidAmount)}</p></div><CheckCircle className="h-8 w-8 text-green-500" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Invoiced</p><p className="text-2xl font-bold font-mono text-blue-600">{formatCurrency(invoicedAmount)}</p></div><TrendingUp className="h-8 w-8 text-blue-500" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Pending</p><p className="text-2xl font-bold font-mono text-yellow-600">{formatCurrency(pendingAmount)}</p></div><Clock className="h-8 w-8 text-yellow-500" /></div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Commission Ledger</CardTitle></CardHeader>
        <CardContent>
          {commissions.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              No commissions recorded yet.
              {isAdmin && <p className="mt-2 text-sm">Click "Auto-Generate" to create commissions for all active listings at 1% rate.</p>}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Listing</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                  {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {commissions.map(c => (
                  <TableRow key={c.id} data-testid={`commission-${c.id}`}>
                    <TableCell className="font-medium text-sm cursor-pointer hover:underline" onClick={() => navigate(c.entity_type === 'mineral' ? `/minerals/${c.deal_id}` : `/deals/${c.deal_id}`)}>
                      {getEntityName(c)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] capitalize gap-1">
                        {c.entity_type === 'mineral' ? <Gem size={10} /> : <Package size={10} />}
                        {c.entity_type || 'deal'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(c.amount)}</TableCell>
                    <TableCell className="text-right font-mono">{c.percentage}%</TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <Select value={c.status} onValueChange={(v) => handleStatusChange(c.id, v)}>
                          <SelectTrigger className="h-7 w-24 text-xs" data-testid={`commission-status-${c.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="invoiced">Invoiced</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="outline" className={`text-[10px] capitalize ${statusColors[c.status] || ''}`}>{c.status}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{c.notes || '-'}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(c.id)} data-testid={`delete-commission-${c.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Commission Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Commission</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Listing Type</Label>
              <Select value={newCommission.entity_type} onValueChange={(v) => setNewCommission({ ...newCommission, entity_type: v, deal_id: '' })}>
                <SelectTrigger data-testid="new-commission-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="deal">Deal</SelectItem>
                  <SelectItem value="mineral">Mineral</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Select {newCommission.entity_type === 'mineral' ? 'Mineral' : 'Deal'}</Label>
              <Select value={newCommission.deal_id} onValueChange={(v) => setNewCommission({ ...newCommission, deal_id: v })}>
                <SelectTrigger data-testid="new-commission-entity"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {entityOptions.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Amount (USD)</Label><Input type="number" value={newCommission.amount} onChange={(e) => setNewCommission({ ...newCommission, amount: e.target.value })} data-testid="new-commission-amount" /></div>
              <div><Label>Rate (%)</Label><Input type="number" step="0.1" value={newCommission.percentage} onChange={(e) => setNewCommission({ ...newCommission, percentage: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Input value={newCommission.notes} onChange={(e) => setNewCommission({ ...newCommission, notes: e.target.value })} placeholder="Optional notes..." /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddDialog(false)}>Cancel</Button>
              <Button onClick={handleAdd} disabled={!newCommission.deal_id || !newCommission.amount} data-testid="save-commission-btn">Create Commission</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
